/**
 * Prefetch de datos y chunks al pasar el mouse por ítems principales del Sidebar,
 * y en idle para listas de productos (categorías, variantes, etc.).
 * Dedupe por sección + tenant para no saturar la red.
 *
 * ⭐ QUÉ FALTABA ACÁ, Y POR QUÉ SE NOTABA
 * El founder reportó que la PRIMERA entrada a una pantalla muestra el spinner varios segundos, y la
 * segunda no. Este archivo ya precargaba dos de las tres cosas que hacen falta —el chunk de la ruta
 * y los números de la barra de KPIs— pero **no la lista que la pantalla va a mostrar**, que es
 * justo la consulta lenta. Se precalentaba todo menos lo que el usuario estaba esperando.
 *
 * El arreglo NO es duplicar acá la consulta de cada pantalla: es llamar **al mismo `loader` de la
 * ruta**. El loader ya es la definición de «qué necesita esta pantalla al abrirse», y reescribirla
 * acá crearía dos definiciones que se desincronizan en silencio — el usuario pagaría DOS consultas
 * (la precalentada que nadie lee y la real) y el síntoma sería idéntico al de no precargar nada.
 *
 * 🪤 Esto sólo funciona porque los loaders **no bloquean**: disparan y devuelven `null`. Si alguno
 * volviera a devolver su promesa, además de congelar la navegación haría que pasar el mouse por el
 * sidebar dejara promesas colgadas. Lo vigila `src/routes/__tests__/loaders.noBloquean.test.tsx`.
 */

import { prefetchSection } from '@/lib/prefetchRouteChunks';
import { cargarProductos } from '@/routes/loaders/productos';
import { cargarOpsVentas } from '@/routes/loaders/opsVentas';
import {
  prefetchAbandonedCartsStats,
  prefetchCategoriesStats,
  prefetchEcommerceDeliveryMethodsStats,
  prefetchEcommerceDeliveryRulesStats,
  prefetchEcommerceOrdersStats,
  prefetchEcommercePagesStats,
  prefetchEcommercePaymentMethodsStats,
  prefetchExistenciasStats,
  prefetchLogisticsCouriersCoverageStats,
  prefetchModifiersStats,
  prefetchProductsStats,
  prefetchSalesOrdersStats,
  prefetchShalomAgenciesStats,
  prefetchTagsStats,
  prefetchTenantDomainsStats,
  prefetchVariantsStats,
} from '@/lib/prefetchStatsPages';

const hoverOnce = new Set<string>();
const idleProductListsOnce = new Set<string>();

type MainNavPrefetchKey =
  | 'products'
  | 'onlineStore'
  | 'settings'
  | 'orders'
  | 'crm'
  | 'callCenter'
  | 'operaciones';

export function prefetchOnMainNavHover(navKey: MainNavPrefetchKey, tenantId: string): void {
  const id = `${navKey}:${tenantId}`;
  if (hoverOnce.has(id)) return;
  hoverOnce.add(id);

  switch (navKey) {
    case 'products':
      prefetchSection('products');
      // ⭐ La LISTA, que es la consulta lenta. Vía el loader de la ruta, para que hover y clic pidan
      // exactamente lo mismo. Si ya está fresca, `ensureQueryData` no hace nada.
      cargarProductos();
      prefetchProductsStats(tenantId).catch(() => {});
      prefetchExistenciasStats(tenantId).catch(() => {});
      prefetchCategoriesStats(tenantId).catch(() => {});
      prefetchModifiersStats(tenantId).catch(() => {});
      prefetchTagsStats(tenantId).catch(() => {});
      prefetchVariantsStats(tenantId).catch(() => {});
      break;
    case 'onlineStore':
      prefetchSection('onlineStore');
      prefetchAbandonedCartsStats(tenantId).catch(() => {});
      prefetchShalomAgenciesStats(tenantId).catch(() => {});
      prefetchEcommercePagesStats(tenantId).catch(() => {});
      break;
    case 'settings':
      prefetchSection('settings');
      prefetchEcommercePaymentMethodsStats(tenantId).catch(() => {});
      prefetchEcommerceDeliveryMethodsStats(tenantId).catch(() => {});
      prefetchEcommerceDeliveryRulesStats(tenantId).catch(() => {});
      prefetchTenantDomainsStats(tenantId).catch(() => {});
      prefetchLogisticsCouriersCoverageStats(tenantId).catch(() => {});
      break;
    case 'orders':
      prefetchSection('orders');
      prefetchEcommerceOrdersStats(tenantId).catch(() => {});
      prefetchSalesOrdersStats(tenantId).catch(() => {});
      prefetchAbandonedCartsStats(tenantId).catch(() => {});
      break;
    case 'crm':
      prefetchSection('crm');
      prefetchSection('callCenter');
      break;
    case 'callCenter':
      prefetchSection('callCenter');
      prefetchSection('crm');
      break;
    case 'operaciones':
      prefetchSection('operaciones');
      // Ventas de Operaciones: la ruta más lenta del panel según el RUM.
      cargarOpsVentas();
      break;
    default:
      break;
  }
}

/**
 * Cuando el hilo está libre (o a los ~4s máx.), precarga listas de productos.
 * Mejora la primera visita a Categorías / Variantes / etc. sin esperar a expandir menú.
 */
export function scheduleIdleProductListPrefetch(tenantId: string): (() => void) | undefined {
  if (idleProductListsOnce.has(tenantId)) return undefined;
  idleProductListsOnce.add(tenantId);

  const run = () => {
    prefetchSection('products');
    prefetchProductsStats(tenantId).catch(() => {});
    prefetchExistenciasStats(tenantId).catch(() => {});
    prefetchCategoriesStats(tenantId).catch(() => {});
    prefetchModifiersStats(tenantId).catch(() => {});
    prefetchTagsStats(tenantId).catch(() => {});
    prefetchVariantsStats(tenantId).catch(() => {});
  };

  if (typeof requestIdleCallback !== 'undefined') {
    const ricId = requestIdleCallback(() => run(), { timeout: 4000 });
    return () => cancelIdleCallback(ricId);
  }
  const t = window.setTimeout(run, 2200);
  return () => clearTimeout(t);
}
